import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useResizeObserver } from '@revelio/core';
import {
  IcicleChart as ReplotsIcicleChart,
  useChartTheme,
} from '@revelio/replots';

import type { SubsidiaryTreeNode } from '../../DataProvider/subsidiaries/expand-subsidiaries';
import {
  type SubsidiaryTreeData,
  SubsidiaryTreeDataProvider,
} from '../../DataProvider/subsidiaries/SubsidiaryTreeDataProvider';
import { useSharedSubsidiaryDrill } from '../../DataProvider/subsidiaries/use-subsidiary-drill';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { ChartFallback } from '../ChartFallback';
import { useDrilldownHeaderBreadcrumb } from '../ChartHeaderBreadcrumb';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { useTableRowsExport } from '../chart-menu/ChartMenuContext';
import { compositionDrilldownOtherColor } from '../utils/composition-drilldown';
import {
  formatIcicleHeadcount,
  formatIcicleTooltipValue,
} from './icicle-format';
import styles from './SubsidiariesIcicle.module.css';
import {
  bucketSubsidiaryView,
  type SubsidiarySizeTransform,
  subsidiaryDrillChildren,
} from './subsidiaries/bucket-subsidiary-tail';
import {
  buildSubsidiaryView,
  indexRawSubsidiaries,
  indexSubsidiaryTree,
  isSubsidiaryDrillable,
  type RawSubsidiaryIndex,
  SUBSIDIARY_ROOT_NODE_ID,
  type SubsidiaryDrillStep,
  subsidiaryAncestry,
  subsidiaryClickTrail,
  subsidiaryDrillSteps,
} from './subsidiaries/build-subsidiary-icicle';

const WIDTH_RATIO = 8;

type ViewRoot = { id: string; node: SubsidiaryTreeNode };

type DrillEntry = SubsidiaryDrillStep & ViewRoot & { color?: string };

type Bucket = (node: SubsidiaryTreeNode) => SubsidiaryTreeNode;

const bucketedViewIndex = (bucket: Bucket, { id, node }: ViewRoot) =>
  indexSubsidiaryTree(bucket(node), id);

const withColor = (entries: DrillEntry[], color: string | undefined) =>
  color === undefined
    ? entries
    : entries.map((entry) =>
        entry.node.isOther === true ? entry : { ...entry, color }
      );

const companyPathOf = (stack: DrillEntry[]): number[] =>
  stack.flatMap((entry) =>
    entry.node.isOther === true ? [] : [entry.node.rcid]
  );

type SubsidiariesIcicleContentProps = {
  tree: SubsidiaryTreeNode;
  raw: RawSubsidiaryIndex;
  sizeTransform: SubsidiarySizeTransform;
  otherColor: string;
};

const SubsidiariesIcicleContent = ({
  tree,
  raw,
  sizeTransform,
  otherColor,
}: SubsidiariesIcicleContentProps) => {
  const { containerRef, height } = useResizeObserver();
  const drawnHeight = Math.floor(height);
  const bucket = useCallback<Bucket>(
    (node) =>
      bucketSubsidiaryView(node, { height: drawnHeight, sizeTransform }),
    [drawnHeight, sizeTransform]
  );

  const [stack, setStack] = useState<DrillEntry[]>([]);
  const drillEntry = stack[stack.length - 1];

  const viewRootFor = useCallback(
    (id: string, node: SubsidiaryTreeNode): ViewRoot => {
      if (node.isOther !== true) {
        return { id, node: raw.get(node.rcid)?.node ?? node };
      }
      const { members: _members, ...other } = node;
      return {
        id,
        node: { ...other, children: subsidiaryDrillChildren(node) },
      };
    },
    [raw]
  );

  const drillSteps = useCallback(
    (id: string, node: SubsidiaryTreeNode) => {
      const next = bucketedViewIndex(bucket, viewRootFor(id, node));
      return subsidiaryDrillSteps(next, id).map((step) => ({
        ...step,
        ...viewRootFor(step.id, next.get(step.id)?.node ?? node),
      }));
    },
    [bucket, viewRootFor]
  );

  const view = useMemo(() => {
    const root = drillEntry ?? { id: SUBSIDIARY_ROOT_NODE_ID, node: tree };
    const index = bucketedViewIndex(bucket, root);
    return {
      index,
      ...buildSubsidiaryView(index, root.id, {
        isDrillable: (node, id) =>
          isSubsidiaryDrillable(bucket(viewRootFor(id, node).node), id),
      }),
    };
  }, [drillEntry, tree, bucket, viewRootFor]);

  const topSkipped = useMemo(
    () =>
      new Set(
        subsidiaryDrillSteps(
          bucketedViewIndex(bucket, {
            id: SUBSIDIARY_ROOT_NODE_ID,
            node: tree,
          }),
          SUBSIDIARY_ROOT_NODE_ID
        )
          .slice(1)
          .map((step) => step.id)
      ),
    [bucket, tree]
  );
  const resolveDrill = useCallback(
    (path: number[], color: string | undefined): DrillEntry[] => {
      for (const rcid of [...path].reverse()) {
        const trail = subsidiaryAncestry(raw, rcid).filter(
          (step) => !topSkipped.has(step.id)
        );
        const target = trail[trail.length - 1];
        if (
          target === undefined ||
          target.node.rcid !== rcid ||
          !isSubsidiaryDrillable(
            bucket(viewRootFor(target.id, target.node).node),
            target.id
          )
        ) {
          continue;
        }
        return withColor(
          [
            ...trail.slice(0, -1).map((step) => ({
              ...viewRootFor(step.id, step.node),
              name: step.node.name,
            })),
            ...drillSteps(target.id, target.node),
          ],
          color
        );
      }
      return [];
    },
    [raw, topSkipped, bucket, viewRootFor, drillSteps]
  );

  const {
    path: sharedPath,
    color: sharedColor,
    share,
  } = useSharedSubsidiaryDrill(tree.rcid);
  useEffect(() => {
    if (sharedPath === undefined) return;
    setStack((previous) => {
      const own = companyPathOf(previous);
      if (isEqual(own, sharedPath)) return previous;
      const next = resolveDrill(sharedPath, sharedColor);
      return isEqual(own, companyPathOf(next)) ? previous : next;
    });
  }, [sharedPath, sharedColor, resolveDrill]);

  const drillTo = useCallback(
    (next: DrillEntry[]) => {
      setStack(next);
      share(companyPathOf(next), next.at(-1)?.color);
    },
    [share]
  );

  const handleBreadcrumbClick = useCallback(
    (targetDepth: number) => drillTo(stack.slice(0, targetDepth)),
    [drillTo, stack]
  );

  useDrilldownHeaderBreadcrumb({
    stack,
    totalLabel: stack.length > 0 ? formatIcicleHeadcount(view.total) : null,
    onNavigate: handleBreadcrumbClick,
  });

  const handleNodeClick = useCallback(
    (node: { id: string; color: string }) => {
      if (view.meta.get(node.id)?.drillable !== true) return;
      const trail = subsidiaryClickTrail(
        view.index,
        (id) => view.meta.has(id),
        node.id,
        raw
      );
      const clicked = trail[trail.length - 1];
      if (clicked === undefined) return;
      const steps = withColor(
        [
          ...trail.slice(0, -1).map((step) => ({
            ...viewRootFor(step.id, step.node),
            name: step.node.name,
          })),
          ...drillSteps(clicked.id, clicked.node),
        ],
        clicked.node.isOther === true ? undefined : node.color
      );
      drillTo([...stack, ...steps]);
    },
    [view, raw, viewRootFor, drillSteps, drillTo, stack]
  );

  const tooltipContent = useCallback(
    (node: { id: string; name: string }) => {
      const info = view.meta.get(node.id);
      const entry = view.index.get(node.id);
      const drawnUnder =
        entry?.parentId == null || !view.meta.has(entry.parentId)
          ? undefined
          : view.index.get(entry.parentId);
      const realParent =
        drawnUnder?.node.isOther === true && entry?.node.isOther !== true
          ? raw.get(entry?.node.rcid ?? Number.NaN)?.parent
          : undefined;
      const value = formatIcicleTooltipValue(info?.total ?? 0, view.total);
      return {
        title: info?.name ?? node.name,
        valueText:
          realParent == null ? value : `${value}  ·  under ${realParent.name}`,
      };
    },
    [view, raw]
  );

  return (
    <div ref={containerRef} className={styles.container}>
      <ReplotsIcicleChart
        data={view.nodes}
        fillColor={drillEntry?.color}
        onNodeClick={handleNodeClick}
        tooltipContent={tooltipContent}
        widthRatio={WIDTH_RATIO}
        sizeTransform={sizeTransform}
        siblingOrder="largest-first"
        otherColor={otherColor}
      />
    </div>
  );
};

const SubsidiaryTreeContent = ({
  tree,
  rootName,
  exportRows,
}: SubsidiaryTreeData) => {
  const { item } = useChart();
  const icicleOptions = item?.plot?.icicle;
  const sizeTransform = icicleOptions?.sizeTransform ?? 'none';
  const { theme } = useChartTheme();
  const otherColor = compositionDrilldownOtherColor(theme);
  const raw = useMemo(() => indexRawSubsidiaries(tree), [tree]);
  const rootView = useMemo(
    () =>
      buildSubsidiaryView(
        indexSubsidiaryTree(bucketSubsidiaryView(tree)),
        SUBSIDIARY_ROOT_NODE_ID
      ),
    [tree]
  );

  useTableRowsExport(exportRows);

  const loneNode = rootView.nodes.length === 1 ? rootView.nodes[0] : undefined;
  if (loneNode !== undefined) {
    const names = [...rootView.skipped.map((step) => step.name), loneNode.name];
    return (
      <ChartNotSupported>
        {names.length === 1
          ? `${rootName} has one subsidiary: ${names[0]}.`
          : `${rootName} has a single chain of subsidiaries: ${names.join(' › ')}.`}
      </ChartNotSupported>
    );
  }

  return (
    <SubsidiariesIcicleContent
      tree={tree}
      raw={raw}
      sizeTransform={sizeTransform}
      otherColor={otherColor}
    />
  );
};

export const SubsidiariesIcicle = () => (
  <SubsidiaryTreeDataProvider>
    {({ data, status }) =>
      data === null ? (
        <ChartFallback status={DataProviderQueryStatus.Success}>
          <ChartNotSupported>
            Select a company to see its subsidiaries.
          </ChartNotSupported>
        </ChartFallback>
      ) : (
        <ChartFallback status={status}>
          {data !== undefined && data.tree.children.length === 0 ? (
            <ChartNotSupported>
              {`${data.rootName} has no subsidiaries in our data.`}
            </ChartNotSupported>
          ) : (
            data !== undefined && (
              <SubsidiaryTreeContent key={data.rcid} {...data} />
            )
          )}
        </ChartFallback>
      )
    }
  </SubsidiaryTreeDataProvider>
);
